import { useDispatch, useSelector } from "react-redux";

import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { trackAddToComparisonClick } from "~/features/compareProducts/utils/analytics";
import { selectProductData } from "~/domains/product/redux/selectors";
import { useTranslation } from "react-i18next";
import { isKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { Button } from "~/common/mui";
import { ExternalBrand } from "~/domains/product/types/ProductDataDTO";
import {
  useAddOrRemoveProductFromComparisonMutation,
  useProductComparisonCount,
  useProductComparisonMembership,
} from "~/features/compareProducts/api/productComparisonQuery";
import { openProductComparisonDrawer } from "./ProductComparisonDrawerContainer";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

interface ProductComparisonCheckboxProps {
  gaLocation: string;
  productCode: string;
  className?: string;
}

export function isProductComparisonCheckboxVisible(product: ProductData) {
  return !isKITProduct(product) && product.externalBrand !== ExternalBrand.Ewellix && !product.externalProduct;
}

export const ProductComparisonCheckbox = ({ productCode, className, gaLocation }: ProductComparisonCheckboxProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const loggedIn = userIsLoggedIn();

  const productData = useSelector(selectProductData(productCode));
  const { isInComparison: isProductAlreadyInComparison } = useProductComparisonMembership(productCode);
  const comparisonMutation = useAddOrRemoveProductFromComparisonMutation();

  const handleAddToComparison = async () => {
    if (!loggedIn) {
      dispatch(openAuthOverlay("login"));
      return;
    }

    const adding = !isProductAlreadyInComparison;
    trackAddToComparisonClick(productData?.designation ?? productCode, adding, gaLocation);
    try {
      const result = await comparisonMutation.mutateAsync({
        productCode,
        isInComparison: isProductAlreadyInComparison,
      });
      if (result.kind === "added") {
        openProductComparisonDrawer();
      }
    } catch (error) {
      console.error("Failed to update product comparison", error);
    }
  };

  return (
    <>
      <CheckboxWithLabel
        tracking-aux-ppr-comparison="true"
        className={className}
        checked={isProductAlreadyInComparison}
        onCheck={handleAddToComparison}
      >
        {t("add.to.comparison.list")}
      </CheckboxWithLabel>
    </>
  );
};

type Props = {
  open?: boolean;
  message?: string;
  apiError?: boolean;
  onClose?: () => void;
};
export const ProductComparisonSnackbar = (props: Props) => {
  const { t } = useTranslation();
  const { count: listItems, data: comparisonData } = useProductComparisonCount();
  const maxSize = comparisonData?.maxSize ?? 0;

  return (
    <Snackbar
      {...props}
      message={t(`${props.message}`, { listItems, maxSize })}
      action={
        <>
          {!props.apiError && (
            <Button
              onClick={openProductComparisonDrawer}
              id="snack_comparison_list"
              color={"inversePrimary"}
              variant={"text"}
              size={"medium"}
            >
              {t("comparisonSnack.view")}
            </Button>
          )}
          <SnackbarCloseButton onClick={props.onClose} />
        </>
      }
    />
  );
};
